import { Button, Card, Dialog, DialogPanel, TextInput } from "@tremor/react";
import { Pause, Play, Plus, Search, Trash2, Users, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  useCreateMonitor,
  useDeleteMonitor,
  useLocations,
  useMonitorTemplates,
  useMonitorsInfinite,
  useUpdateMonitor,
} from "../queries/monitors.js";
import {
  useChannels,
  useSetMonitorChannels,
} from "../queries/channels.js";
import Badge from "../components/Badge.jsx";
import { usePermission } from "../lib/usePermission.js";
import { fmtInterval } from "../utils/format.js";
import { useTranslation } from "react-i18next";
import {
  SeoHealthOptions,
  BrokenLinksOptions,
  ImageOptimizationOptions,
  PerformanceBudgetOptions,
  SuspiciousScriptOptions,
  CarbonFootprintOptions,
  ViewportSelect,
} from "../components/monitor/MonitorTypeOptions.jsx";

const TYPE_OPTIONS = [
  "http",
  "ssl",
  "domain",
  "dns",
  "tcp",
  "ping",
  "port",
  "api",
  "cron",
  "playwright",
  "shopify",
  "wordpress",
  "visual_change",
  "page_speed",
  "security_headers",
  "seo_health",
  "broken_links",
  "image_optimization",
  "performance_budget",
  "suspicious_script",
  "platform_fingerprint",
  "carbon_footprint",
  "joomla",
  "drupal",
  "wix",
  "webflow",
  "screenshot",
];
// Friendly display names for the type picker. Falls back to upper-cased key.
const TYPE_LABELS = {
  http: "HTTP",
  ssl: "SSL",
  domain: "Domain",
  dns: "DNS",
  tcp: "TCP",
  ping: "Ping",
  port: "Port",
  api: "API",
  cron: "Cron",
  playwright: "Playwright",
  shopify: "Shopify",
  wordpress: "WordPress",
  visual_change: "Visual change",
  page_speed: "Page speed",
  security_headers: "Security headers",
  seo_health: "SEO health",
  broken_links: "Broken links",
  image_optimization: "Image optimization",
  performance_budget: "Performance budget",
  suspicious_script: "Suspicious content",
  platform_fingerprint: "Platform fingerprint",
  carbon_footprint: "Carbon footprint",
  joomla: "Joomla",
  drupal: "Drupal",
  wix: "Wix",
  webflow: "Webflow",
  screenshot: "Screenshot history",
};
const typeLabel = (t) => TYPE_LABELS[t] ?? t.toUpperCase();

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD"];
const METHODS_WITH_BODY = ["POST", "PUT", "PATCH"];

const INTERVAL_OPTIONS = [
  { labelKey: "intervals.seconds", labelCount: 30, value: 30 },
  { labelKey: "intervals.minute", value: 60 },
  { labelKey: "intervals.minutes", labelCount: 5, value: 300 },
  { labelKey: "intervals.minutes", labelCount: 10, value: 600 },
  { labelKey: "intervals.minutes", labelCount: 30, value: 1800 },
  { labelKey: "intervals.hour", value: 3600 },
  { labelKey: "intervals.hours", labelCount: 2, value: 7200 },
  { labelKey: "intervals.hours", labelCount: 4, value: 14400 },
  { labelKey: "intervals.hours", labelCount: 12, value: 43200 },
  { labelKey: "intervals.day", value: 86400 },
  { labelKey: "intervals.week", value: 604800 },
  { labelKey: "intervals.custom", value: 0 },
];

const INTERVAL_PRESET_VALUES = new Set(
  INTERVAL_OPTIONS.filter((o) => o.value > 0).map((o) => o.value)
);

// Recommended default interval per check type. Kept in sync with
// lib/monitor_intervals.py on the server.
const TYPE_DEFAULT_INTERVAL = {
  http: 60,
  api: 60,
  tcp: 60,
  port: 60,
  ping: 60,
  cron: 60,
  dns: 300,
  playwright: 300,
  shopify: 300,
  wordpress: 300,
  ssl: 86400,
  domain: 86400,
  visual_change: 600,
  page_speed: 600,
  security_headers: 3600,
  seo_health: 21600,
  broken_links: 86400,
  image_optimization: 21600,
  performance_budget: 3600,
  suspicious_script: 3600,
  platform_fingerprint: 86400,
  carbon_footprint: 86400,
  joomla: 3600,
  drupal: 3600,
  wix: 3600,
  webflow: 3600,
  screenshot: 21600,
};
const defaultIntervalFor = (type) => TYPE_DEFAULT_INTERVAL[type] ?? 60;

const CUSTOM_UNITS = [
  { labelKey: "intervals.minutes", seconds: 60 },
  { labelKey: "intervals.hours", seconds: 3600 },
  { labelKey: "intervals.days", seconds: 86400 },
  { labelKey: "intervals.weeks", seconds: 604800 },
];

function IntervalSelect({ value, onChange }) {
  const { t } = useTranslation("monitors");
  const [customMode, setCustomMode] = useState(!INTERVAL_PRESET_VALUES.has(value));

  // Decompose seconds into a sensible amount + unit pair
  function decompose(secs) {
    for (const unit of [...CUSTOM_UNITS].reverse()) {
      if (secs % unit.seconds === 0 && secs / unit.seconds >= 1) {
        return { amount: secs / unit.seconds, unit: unit.seconds };
      }
    }
    return { amount: Math.round(secs / 60), unit: 60 };
  }

  const { amount: customAmount, unit: customUnit } = decompose(value);

  return (
    <div className="space-y-2">
      <select
        value={customMode ? 0 : value}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (v > 0) {
            setCustomMode(false);
            onChange(v);
          } else {
            setCustomMode(true);
            // Keep current value; user adjusts via the custom inputs below
          }
        }}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
      >
        {INTERVAL_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {t(o.labelKey, { count: o.labelCount })}
          </option>
        ))}
      </select>
      {customMode && (
        <div className="flex gap-2">
          <input
            type="number"
            min={1}
            value={customAmount}
            onChange={(e) => onChange(Math.max(1, Number(e.target.value)) * customUnit)}
            className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
          <select
            value={customUnit}
            onChange={(e) => onChange(customAmount * Number(e.target.value))}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            {CUSTOM_UNITS.map((u) => (
              <option key={u.seconds} value={u.seconds}>
                {t(u.labelKey, { count: 2 })}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

const TARGET_PLACEHOLDER = {
  http: "https://example.com",
  ssl: "example.com",
  domain: "example.com",
  dns: "example.com",
  tcp: "example.com:80",
  ping: "8.8.8.8 or example.com",
  port: "example.com:22",
  api: "https://api.example.com/health",
  cron: "Nightly backup job",
  playwright: "https://example.com",
  shopify: "https://store.example.com",
  wordpress: "https://myblog.example.com",
  visual_change: "https://example.com/pricing",
  page_speed: "https://example.com",
  security_headers: "https://example.com",
  seo_health: "https://example.com/page",
  broken_links: "https://example.com",
  image_optimization: "https://example.com",
  performance_budget: "https://example.com",
  suspicious_script: "https://example.com",
  platform_fingerprint: "https://example.com",
  carbon_footprint: "https://example.com",
  joomla: "https://example.com",
  drupal: "https://example.com",
  wix: "https://example.com",
  webflow: "https://example.com",
  screenshot: "https://example.com",
};

// Types that run on agents (have locations); cron is server-side
const AGENT_TYPES = new Set([
  "http",
  "ssl",
  "domain",
  "dns",
  "tcp",
  "ping",
  "port",
  "api",
  "playwright",
  "shopify",
  "wordpress",
  "visual_change",
  "page_speed",
  "security_headers",
  "seo_health",
  "broken_links",
  "image_optimization",
  "performance_budget",
  "suspicious_script",
  "platform_fingerprint",
  "carbon_footprint",
  "joomla",
  "drupal",
  "wix",
  "webflow",
  "screenshot",
]);

const EMPTY_FORM = {
  name: "",
  type: "http",
  target: "",
  interval_seconds: 60,
  locations: [],
  options: { confirmation_count: 2 },
};

function normalizeTemplateToForm(template, defaultLocations = []) {
  return {
    name: template.name,
    type: template.type,
    target: template.target,
    interval_seconds: template.interval_seconds ?? 60,
    locations: template.type === "cron" ? [] : defaultLocations,
    options: { ...(template.options ?? {}) },
  };
}

function flattenTemplates(templatesByCategory, t) {
  return Object.entries(templatesByCategory ?? {}).flatMap(([category, templates]) =>
    templates.map((template) => ({
      ...template,
      categoryLabel: t(`templates.categories.${category}`) ?? category,
    }))
  );
}

function TemplatePicker({ templatesByCategory, onUseTemplate }) {
  const { t } = useTranslation("monitors");
  const templates = flattenTemplates(templatesByCategory, t);

  if (templates.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("templates.title")}
          </p>
          <p className="mt-1 text-sm text-gray-500">{t("templates.subtitle")}</p>
        </div>
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("templates.choose")}
        </label>
        <select
          defaultValue=""
          onChange={(e) => {
            const selected = templates.find(
              (template) => template.id === e.target.value
            );
            if (!selected) {
              return;
            }
            onUseTemplate(selected);
            e.target.value = "";
          }}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          <option value="">{t("templates.scratch")}</option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.categoryLabel} - {template.name}
            </option>
          ))}
        </select>
        <p className="mt-2 text-xs text-gray-400">{t("templates.help")}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-sections for type-specific options
// ---------------------------------------------------------------------------

function HttpOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  const method = options.method ?? "GET";
  const showBody = METHODS_WITH_BODY.includes(method);
  const authType = options._auth_type ?? "none";

  function setAuthType(type) {
    // Clear old auth fields when switching
    onChange("_auth_type", type);
    onChange("basic_auth", undefined);
    onChange("bearer_token", undefined);
  }

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("options.http.title")}
      </p>

      {/* Method */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.http.method")}
        </label>
        <select
          value={method}
          onChange={(e) => onChange("method", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          {HTTP_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {/* Body */}
      {showBody && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              {t("options.http.body_type")}
            </label>
            <select
              value={options.body_type ?? "json"}
              onChange={(e) => onChange("body_type", e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            >
              <option value="json">JSON</option>
              <option value="form">{t("options.http.form_encoded")}</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              {t("options.http.body")}
            </label>
            <textarea
              rows={4}
              value={options.body ?? ""}
              onChange={(e) => onChange("body", e.target.value)}
              placeholder={
                (options.body_type ?? "json") === "json"
                  ? '{"key": "value"}'
                  : "key=value&other=data"
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
            />
          </div>
        </>
      )}

      {/* Authentication */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.http.auth")}
        </label>
        <select
          value={authType}
          onChange={(e) => setAuthType(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          <option value="none">{t("options.http.auth_none")}</option>
          <option value="basic">{t("options.http.auth_basic")}</option>
          <option value="bearer">{t("options.http.auth_bearer")}</option>
        </select>
      </div>
      {authType === "basic" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              {t("options.http.username")}
            </label>
            <input
              type="text"
              value={options.basic_auth?.username ?? ""}
              onChange={(e) =>
                onChange("basic_auth", {
                  ...options.basic_auth,
                  username: e.target.value,
                })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              {t("options.http.password")}
            </label>
            <input
              type="password"
              value={options.basic_auth?.password ?? ""}
              onChange={(e) =>
                onChange("basic_auth", {
                  ...options.basic_auth,
                  password: e.target.value,
                })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            />
          </div>
        </div>
      )}
      {authType === "bearer" && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("options.http.token")}
          </label>
          <input
            type="text"
            value={options.bearer_token ?? ""}
            onChange={(e) => onChange("bearer_token", e.target.value)}
            placeholder="eyJhbGci..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs outline-none focus:border-teal-600"
          />
        </div>
      )}

      {/* Keyword validation */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.http.keyword_present")}
        </label>
        <input
          type="text"
          value={options.keyword ?? ""}
          onChange={(e) => onChange("keyword", e.target.value || undefined)}
          placeholder="e.g. Welcome, OK, status: up"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-0.5 text-xs text-gray-400">
          {t("options.http.keyword_present_help")}
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.http.keyword_absent")}
        </label>
        <input
          type="text"
          value={options.keyword_absent ?? ""}
          onChange={(e) => onChange("keyword_absent", e.target.value || undefined)}
          placeholder="e.g. Error, Maintenance, 503"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-0.5 text-xs text-gray-400">
          {t("options.http.keyword_absent_help")}
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.http.keyword_regex")}
        </label>
        <input
          type="text"
          value={options.keyword_regex ?? ""}
          onChange={(e) => onChange("keyword_regex", e.target.value || undefined)}
          placeholder='e.g. "status":\s*"ok"'
          className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs outline-none focus:border-teal-600"
        />
        <p className="mt-0.5 text-xs text-gray-400">
          {t("options.http.keyword_regex_help")}
        </p>
      </div>

      {/* Timeout */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.http.timeout")}
        </label>
        <input
          type="number"
          min={1}
          max={60}
          value={options.timeout_seconds ?? 10}
          onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
          className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
      </div>
    </div>
  );
}

function SslOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("options.ssl.title")}
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("options.ssl.warn_days")}
          </label>
          <input
            type="number"
            min={1}
            value={options.warning_days ?? 14}
            onChange={(e) => onChange("warning_days", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("options.ssl.critical_days")}
          </label>
          <input
            type="number"
            min={1}
            value={options.critical_days ?? 3}
            onChange={(e) => onChange("critical_days", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
      </div>
    </div>
  );
}

function DomainOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("options.domain.title")}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("options.ssl.warn_days")}
          </label>
          <input
            type="number"
            min={1}
            value={options.warning_days ?? 30}
            onChange={(e) => onChange("warning_days", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("options.ssl.critical_days")}
          </label>
          <input
            type="number"
            min={1}
            value={options.critical_days ?? 7}
            onChange={(e) => onChange("critical_days", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
      </div>
      <p className="text-xs text-gray-400">{t("options.domain.help")}</p>
    </div>
  );
}

function DnsOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  const expectedRaw = (options.expected_values ?? []).join(", ");
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("options.dns.title")}
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.dns.record_type")}
        </label>
        <select
          value={options.record_type ?? "A"}
          onChange={(e) => onChange("record_type", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          {["A", "AAAA", "CNAME", "MX", "TXT"].map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.dns.expected_values")}{" "}
          <span className="font-normal text-gray-400">
            {t("common:common.optional_comma")}
          </span>
        </label>
        <input
          type="text"
          value={expectedRaw}
          onChange={(e) =>
            onChange(
              "expected_values",
              e.target.value
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean)
            )
          }
          placeholder={t("options.dns.expected_values_placeholder")}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-400">
          {t("options.dns.expected_values_help")}
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.dns.custom_nameserver")}
        </label>
        <input
          type="text"
          value={options.nameserver ?? ""}
          onChange={(e) => onChange("nameserver", e.target.value || undefined)}
          placeholder="8.8.8.8"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
      </div>
    </div>
  );
}

function TcpOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("options.tcp.title")}
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.tcp.port")}
        </label>
        <input
          type="number"
          min={1}
          max={65535}
          value={options.port ?? ""}
          onChange={(e) =>
            onChange("port", e.target.value ? Number(e.target.value) : undefined)
          }
          placeholder="80"
          className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-400">{t("options.tcp.port_help")}</p>
      </div>
    </div>
  );
}

function PingOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("options.ping.title")}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("options.ping.packets")}
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={options.count ?? 3}
            onChange={(e) => onChange("count", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("options.ping.timeout")}
          </label>
          <input
            type="number"
            min={1}
            max={30}
            value={options.timeout_seconds ?? 10}
            onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
      </div>
    </div>
  );
}

function PortOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("options.port.title")}
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.port.expected_state")}
        </label>
        <select
          value={options.expected_state ?? "open"}
          onChange={(e) => onChange("expected_state", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          <option value="open">{t("options.port.expected_open")}</option>
          <option value="closed">{t("options.port.expected_closed")}</option>
        </select>
        <p className="mt-1 text-xs text-gray-400">{t("options.port.expected_help")}</p>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.tcp.port")}
        </label>
        <input
          type="number"
          min={1}
          max={65535}
          value={options.port ?? ""}
          onChange={(e) =>
            onChange("port", e.target.value ? Number(e.target.value) : undefined)
          }
          placeholder="22"
          className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-400">{t("options.tcp.port_help")}</p>
      </div>
    </div>
  );
}

const ASSERTION_OPERATORS = {
  status_code: ["eq", "neq", "lt", "gt", "in"],
  header: ["eq", "neq", "contains", "regex"],
  jsonpath: ["eq", "neq", "contains", "regex"],
  body_regex: [],
};

const OPERATOR_LABELS = {
  eq: "=",
  neq: "≠",
  lt: "<",
  gt: ">",
  in: "in list",
  contains: "contains",
  regex: "matches regex",
};

function AssertionRow({ assertion, onChange, onRemove }) {
  const { t } = useTranslation("monitors");
  const atype = assertion.type ?? "status_code";
  const operators = ASSERTION_OPERATORS[atype] ?? [];

  return (
    <div className="flex flex-wrap items-start gap-2 rounded-md border border-gray-200 bg-white p-2">
      <select
        value={atype}
        onChange={(e) => onChange({ type: e.target.value, operator: "eq", value: "" })}
        className="rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
      >
        <option value="status_code">{t("options.api.status_code")}</option>
        <option value="header">{t("options.api.header")}</option>
        <option value="jsonpath">{t("options.api.jsonpath")}</option>
        <option value="body_regex">{t("options.api.body_regex")}</option>
      </select>

      {atype === "header" && (
        <input
          type="text"
          placeholder={t("options.api.header")}
          value={assertion.name ?? ""}
          onChange={(e) => onChange({ ...assertion, name: e.target.value })}
          className="w-36 rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
        />
      )}

      {atype === "jsonpath" && (
        <input
          type="text"
          placeholder="$.status"
          value={assertion.path ?? ""}
          onChange={(e) => onChange({ ...assertion, path: e.target.value })}
          className="w-36 rounded border border-gray-300 px-2 py-1 font-mono text-xs outline-none focus:border-teal-600"
        />
      )}

      {operators.length > 0 && (
        <select
          value={assertion.operator ?? "eq"}
          onChange={(e) => onChange({ ...assertion, operator: e.target.value })}
          className="rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
        >
          {operators.map((op) => (
            <option key={op} value={op}>
              {OPERATOR_LABELS[op] ?? op}
            </option>
          ))}
        </select>
      )}

      {atype === "body_regex" ? (
        <input
          type="text"
          placeholder="pattern"
          value={assertion.pattern ?? ""}
          onChange={(e) => onChange({ ...assertion, pattern: e.target.value })}
          className="flex-1 rounded border border-gray-300 px-2 py-1 font-mono text-xs outline-none focus:border-teal-600"
        />
      ) : atype === "status_code" && assertion.operator === "in" ? (
        <input
          type="text"
          placeholder="200, 201, 202"
          value={(assertion.values ?? []).join(", ")}
          onChange={(e) =>
            onChange({
              ...assertion,
              values: e.target.value
                .split(",")
                .map((v) => parseInt(v.trim(), 10))
                .filter((n) => !isNaN(n)),
            })
          }
          className="flex-1 rounded border border-gray-300 px-2 py-1 font-mono text-xs outline-none focus:border-teal-600"
        />
      ) : (
        <input
          type={atype === "status_code" ? "number" : "text"}
          placeholder={atype === "status_code" ? "200" : "expected value"}
          value={assertion.value ?? ""}
          onChange={(e) =>
            onChange({
              ...assertion,
              value: atype === "status_code" ? Number(e.target.value) : e.target.value,
            })
          }
          className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
        />
      )}

      <button
        type="button"
        onClick={onRemove}
        className="ml-auto rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ApiOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  const method = options.method ?? "GET";
  const showBody = METHODS_WITH_BODY.includes(method);
  const assertions = options.assertions ?? [];

  function updateAssertion(idx, updated) {
    const next = assertions.map((a, i) => (i === idx ? updated : a));
    onChange("assertions", next);
  }

  function removeAssertion(idx) {
    onChange(
      "assertions",
      assertions.filter((_, i) => i !== idx)
    );
  }

  function addAssertion() {
    onChange("assertions", [
      ...assertions,
      { type: "status_code", operator: "eq", value: 200 },
    ]);
  }

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("options.api.title")}
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.http.method")}
        </label>
        <select
          value={method}
          onChange={(e) => onChange("method", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          {HTTP_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {showBody && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              {t("options.http.body_type")}
            </label>
            <select
              value={options.body_type ?? "json"}
              onChange={(e) => onChange("body_type", e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            >
              <option value="json">JSON</option>
              <option value="form">{t("options.http.form_encoded")}</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              {t("options.http.body")}
            </label>
            <textarea
              rows={3}
              value={options.body ?? ""}
              onChange={(e) => onChange("body", e.target.value)}
              placeholder='{"key": "value"}'
              className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs outline-none focus:border-teal-600"
            />
          </div>
        </>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.http.timeout")}
        </label>
        <input
          type="number"
          min={1}
          max={60}
          value={options.timeout_seconds ?? 10}
          onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
          className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-xs font-medium text-gray-600">
            {t("options.api.assertions")}
          </label>
          <button
            type="button"
            onClick={addAssertion}
            className="rounded px-2 py-0.5 text-xs font-medium text-teal-600 hover:bg-teal-50"
          >
            {t("options.api.add_assertion")}
          </button>
        </div>
        {assertions.length === 0 && (
          <p className="text-xs text-gray-400">{t("options.api.no_assertions")}</p>
        )}
        <div className="space-y-1.5">
          {assertions.map((a, i) => (
            <AssertionRow
              key={i}
              assertion={a}
              onChange={(updated) => updateAssertion(i, updated)}
              onRemove={() => removeAssertion(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CronOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("options.cron.title")}
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.cron.schedule")}
        </label>
        <input
          type="text"
          placeholder="*/5 * * * *"
          value={options.cron_schedule ?? ""}
          onChange={(e) => onChange("cron_schedule", e.target.value || undefined)}
          className="w-48 rounded-md border border-gray-300 px-3 py-2 font-mono text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-400">{t("options.cron.schedule_help")}</p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("options.cron.grace")}
        </label>
        <input
          type="number"
          min={0}
          value={options.grace_period_seconds ?? 0}
          onChange={(e) => onChange("grace_period_seconds", Number(e.target.value))}
          className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-400">{t("options.cron.grace_help")}</p>
      </div>

      <div>
        <p className="mb-2 text-xs font-medium text-gray-600">
          {t("options.cron.assert_title")}{" "}
          <span className="font-normal text-gray-400">
            {t("options.cron.assert_help_main")}
          </span>
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              {t("options.cron.assert_key")}
            </label>
            <input
              type="text"
              placeholder="status"
              value={options.assert_key ?? ""}
              onChange={(e) => onChange("assert_key", e.target.value || undefined)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              {t("options.cron.assert_value")}
            </label>
            <input
              type="text"
              placeholder="ok"
              value={options.assert_value ?? ""}
              onChange={(e) => onChange("assert_value", e.target.value || undefined)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            />
          </div>
        </div>
        <p className="mt-1 text-xs text-gray-400">
          {t("options.cron.assert_help_footer")}
        </p>
      </div>
    </div>
  );
}

function StepRow({ step, index, total, onChange, onRemove, onMoveUp, onMoveDown }) {
  const { t } = useTranslation("monitors");
  function field(key, value) {
    onChange({ ...step, [key]: value });
  }
  return (
    <div className="rounded border border-gray-200 bg-white p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className="w-5 text-center text-xs font-semibold text-gray-400">
          {index + 1}
        </span>
        <select
          value={step.action}
          onChange={(e) => onChange({ action: e.target.value })}
          className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        >
          {Object.entries(t("step_actions", { returnObjects: true })).map(
            ([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            )
          )}
        </select>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={index === 0}
            onClick={onMoveUp}
            className="rounded px-1.5 py-1 text-xs text-gray-400 hover:bg-gray-100 disabled:opacity-30"
          >
            ▲
          </button>
          <button
            type="button"
            disabled={index === total - 1}
            onClick={onMoveDown}
            className="rounded px-1.5 py-1 text-xs text-gray-400 hover:bg-gray-100 disabled:opacity-30"
          >
            ▼
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded px-1.5 py-1 text-xs text-red-400 hover:bg-red-50"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Action-specific fields */}
      {step.action === "goto" && (
        <input
          placeholder="https://example.com/page"
          value={step.url ?? ""}
          onChange={(e) => field("url", e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        />
      )}
      {(step.action === "click" || step.action === "assert_visible") && (
        <input
          placeholder="CSS selector, e.g. button#submit"
          value={step.selector ?? ""}
          onChange={(e) => field("selector", e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        />
      )}
      {step.action === "fill" && (
        <div className="space-y-1.5">
          <input
            placeholder="CSS selector"
            value={step.selector ?? ""}
            onChange={(e) => field("selector", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
          <input
            placeholder="Value to type"
            value={step.value ?? ""}
            onChange={(e) => field("value", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
        </div>
      )}
      {step.action === "wait" && (
        <div className="space-y-1.5">
          <input
            placeholder="CSS selector (optional)"
            value={step.selector ?? ""}
            onChange={(e) => field("selector", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={100}
              max={30000}
              placeholder="Timeout ms"
              value={step.timeout_ms ?? ""}
              onChange={(e) => field("timeout_ms", Number(e.target.value))}
              className="w-28 rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
            />
            <span className="text-xs text-gray-400">ms</span>
          </div>
        </div>
      )}
      {step.action === "assert_url" && (
        <input
          placeholder="URL must contain this string"
          value={step.contains ?? ""}
          onChange={(e) => field("contains", e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        />
      )}
      {step.action === "assert_text" && (
        <div className="space-y-1.5">
          <input
            placeholder="CSS selector"
            value={step.selector ?? ""}
            onChange={(e) => field("selector", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
          <input
            placeholder="Expected text (substring match)"
            value={step.text ?? ""}
            onChange={(e) => field("text", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
        </div>
      )}
      {step.action === "screenshot" && (
        <input
          placeholder="Name (e.g. after-login)"
          value={step.name ?? ""}
          onChange={(e) => field("name", e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        />
      )}
    </div>
  );
}

function PlaywrightOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  const steps = options.steps ?? [];

  function updateSteps(newSteps) {
    onChange("steps", newSteps.length > 0 ? newSteps : undefined);
  }

  function addStep() {
    updateSteps([...steps, { action: "goto", url: "" }]);
  }

  function updateStep(i, updated) {
    const next = [...steps];
    next[i] = updated;
    updateSteps(next);
  }

  function removeStep(i) {
    updateSteps(steps.filter((_, idx) => idx !== i));
  }

  function moveStep(i, dir) {
    const next = [...steps];
    const j = i + dir;
    [next[i], next[j]] = [next[j], next[i]];
    updateSteps(next);
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t("options.browser.title")}
        </p>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("options.browser.wait_until")}
          </label>
          <select
            value={options.wait_until ?? "networkidle"}
            onChange={(e) => onChange("wait_until", e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            <option value="networkidle">{t("options.browser.wait_network")}</option>
            <option value="load">{t("options.browser.wait_load")}</option>
            <option value="domcontentloaded">{t("options.browser.wait_dom")}</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("options.http.timeout")}
          </label>
          <input
            type="number"
            min={10}
            max={120}
            value={options.timeout_seconds ?? 30}
            onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
            className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={options.full_page ?? false}
            onChange={(e) => onChange("full_page", e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
          />
          {t("options.browser.full_page")}
        </label>
      </div>

      {/* Transaction steps */}
      <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">
              {t("options.browser.steps_title")}
            </p>
            <p className="mt-0.5 text-xs text-teal-600">
              {steps.length === 0
                ? t("options.browser.no_steps")
                : t("options.browser.steps_count", { count: steps.length })}
            </p>
          </div>
          <button
            type="button"
            onClick={addStep}
            className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-teal-700"
          >
            {t("options.browser.add_step")}
          </button>
        </div>

        {steps.length > 0 && (
          <div className="space-y-2">
            {steps.map((step, i) => (
              <StepRow
                key={i}
                step={step}
                index={i}
                total={steps.length}
                onChange={(updated) => updateStep(i, updated)}
                onRemove={() => removeStep(i)}
                onMoveUp={() => moveStep(i, -1)}
                onMoveDown={() => moveStep(i, 1)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PageSpeedOptions({ options, onChange }) {
  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Page speed
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Wait until
        </label>
        <select
          value={options.wait_until ?? "load"}
          onChange={(e) => onChange("wait_until", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          <option value="load">Page load event</option>
          <option value="domcontentloaded">DOM ready</option>
          <option value="networkidle">Network idle</option>
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Alert when score drops below
        </label>
        <input
          type="number"
          min={0}
          max={100}
          step={1}
          value={options.score_threshold ?? 60}
          onChange={(e) =>
            onChange(
              "score_threshold",
              Math.max(0, Math.min(100, Number(e.target.value)))
            )
          }
          className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-500">
          The score combines TTFB, FCP, LCP, CLS, and INP using Lighthouse-style
          weights.
        </p>
      </div>

      <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />

      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={options.full_page ?? false}
          onChange={(e) => onChange("full_page", e.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
        />
        Capture full-page screenshot (slower)
      </label>
    </div>
  );
}

function VisualChangeOptions({ options, onChange }) {
  const mode = options.mode ?? "visual";
  const ignore = options.ignore_selectors ?? [];
  const keywords = options.keywords ?? [];

  function setListField(field, raw) {
    const parts = raw
      .split(/\r?\n|,/)
      .map((s) => s.trim())
      .filter(Boolean);
    onChange(field, parts.length ? parts : undefined);
  }

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Visual change detection
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Mode</label>
        <select
          value={mode}
          onChange={(e) => onChange("mode", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          <option value="visual">Visual (pixel diff)</option>
          <option value="text">Text only</option>
          <option value="element">Element (CSS selector)</option>
          <option value="keyword_change">Keyword presence</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          {mode === "visual" &&
            "Screenshot the page and alert when more than threshold % of pixels change."}
          {mode === "text" &&
            "Compare extracted visible text — ignores styling changes."}
          {mode === "element" &&
            "Restrict the diff to one selector — useful for pricing widgets etc."}
          {mode === "keyword_change" &&
            "Alert when specific words appear or disappear."}
        </p>
      </div>

      {(mode === "element" || mode === "visual") && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {mode === "element"
              ? "Selector (required)"
              : "Selector (optional — leave blank for full page)"}
          </label>
          <input
            type="text"
            value={options.selector ?? ""}
            onChange={(e) => onChange("selector", e.target.value || undefined)}
            placeholder=".product-price, #main, [data-testid=hero]"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono outline-none focus:border-teal-600"
          />
        </div>
      )}

      {(mode === "visual" || mode === "element") && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Change threshold (%)
          </label>
          <input
            type="number"
            min={0.1}
            max={100}
            step={0.1}
            value={options.threshold_percent ?? 2}
            onChange={(e) => onChange("threshold_percent", Number(e.target.value))}
            className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Ignore selectors (one per line)
        </label>
        <textarea
          rows={3}
          value={ignore.join("\n")}
          onChange={(e) => setListField("ignore_selectors", e.target.value)}
          placeholder=".cookie-banner&#10;#chat-widget&#10;.timestamp"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-500">
          Elements matching these selectors are hidden before the screenshot so dynamic
          widgets don't trigger false alerts.
        </p>
      </div>

      {mode === "keyword_change" && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Watched keywords (one per line)
          </label>
          <textarea
            rows={3}
            value={keywords.join("\n")}
            onChange={(e) => setListField("keywords", e.target.value)}
            placeholder="Sold out&#10;Discontinued&#10;Out of stock"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono outline-none focus:border-teal-600"
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Alert on
          </label>
          <select
            value={options.alert_on ?? "threshold_exceeded"}
            onChange={(e) => onChange("alert_on", e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            <option value="threshold_exceeded">Threshold exceeded</option>
            <option value="any_change">Any change</option>
            <option value="keyword_found">Keyword appeared</option>
            <option value="keyword_missing">Keyword disappeared</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Baseline policy
          </label>
          <select
            value={options.baseline_policy ?? "first_run"}
            onChange={(e) => onChange("baseline_policy", e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            <option value="first_run">Auto-roll on first run</option>
            <option value="manual_approve">Require manual approval</option>
          </select>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Wait until
        </label>
        <select
          value={options.wait_until ?? "load"}
          onChange={(e) => onChange("wait_until", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          <option value="load">Page load event</option>
          <option value="domcontentloaded">DOM ready</option>
          <option value="networkidle">Network idle</option>
        </select>
      </div>

      <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />

      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={options.full_page ?? true}
          onChange={(e) => onChange("full_page", e.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
        />
        Capture full page (scroll)
      </label>
    </div>
  );
}

function ShopifyOptions({ options, onChange }) {
  const { t } = useTranslation("monitors");
  return (
    <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
        {t("options.shopify.title")}
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Product URL <span className="font-normal text-gray-400">(recommended)</span>
        </label>
        <input
          type="text"
          value={options.product_url ?? ""}
          onChange={(e) => onChange("product_url", e.target.value || undefined)}
          placeholder="https://store.example.com/products/example-product"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-400">
          If left blank, Monitero tries to discover a product page automatically.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Collection URL{" "}
          <span className="font-normal text-gray-400">(optional fallback)</span>
        </label>
        <input
          type="text"
          value={options.collection_url ?? ""}
          onChange={(e) => onChange("collection_url", e.target.value || undefined)}
          placeholder="https://store.example.com/collections/all"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Timeout (seconds)
          </label>
          <input
            type="number"
            min={10}
            max={120}
            value={options.timeout_seconds ?? 30}
            onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Wait until
          </label>
          <select
            value={options.wait_until ?? "load"}
            onChange={(e) => onChange("wait_until", e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            <option value="load">Load event</option>
            <option value="domcontentloaded">DOMContentLoaded</option>
            <option value="networkidle">Network idle</option>
          </select>
        </div>

        <label className="flex items-center gap-2 rounded-md border border-emerald-100 bg-white px-3 py-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={options.check_checkout ?? true}
            onChange={(e) => onChange("check_checkout", e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
          />
          Verify checkout handoff
        </label>
      </div>
      <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />
    </div>
  );
}

function WordPressOptions({ options, onChange }) {
  return (
    <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
        WordPress Monitor Options
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Timeout (seconds)
          </label>
          <input
            type="number"
            min={10}
            max={120}
            value={options.timeout_seconds ?? 30}
            onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Wait until
          </label>
          <select
            value={options.wait_until ?? "networkidle"}
            onChange={(e) => onChange("wait_until", e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            <option value="networkidle">Network idle</option>
            <option value="load">Load event</option>
            <option value="domcontentloaded">DOMContentLoaded</option>
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 rounded-md border border-blue-100 bg-white px-3 py-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={options.check_rest_api ?? true}
          onChange={(e) => onChange("check_rest_api", e.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
        />
        Check REST API health
      </label>
      <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared locations checkbox grid
// ---------------------------------------------------------------------------

function LocationsField({ locations, onChange }) {
  const { t } = useTranslation("monitors");
  const { data: availableLocations = [] } = useLocations();
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">
        {t("locations_field.label")}
      </label>
      <div className="mt-1 grid grid-cols-2 gap-1.5">
        {availableLocations.map((loc) => (
          <label
            key={loc.slug}
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
          >
            <input
              type="checkbox"
              checked={locations.includes(loc.slug)}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? [...locations, loc.slug]
                    : locations.filter((x) => x !== loc.slug)
                )
              }
              className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
            />
            <span className="font-mono text-xs">{loc.label}</span>
          </label>
        ))}
      </div>
      {locations.length === 0 && (
        <p className="mt-1 text-xs text-red-500">{t("locations_field.empty_error")}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New monitor drawer (slides in from the right)
// ---------------------------------------------------------------------------

function NewMonitorDrawer({
  templatesByCategory,
  onClose,
  onCreated,
  initialType,
  initialTarget,
}) {
  const { t } = useTranslation(["monitors", "common"]);
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    type: initialType || EMPTY_FORM.type,
    target: initialTarget || EMPTY_FORM.target,
  }));
  const [createdCronToken, setCreatedCronToken] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [defaultLocations, setDefaultLocations] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  const { data: locations } = useLocations();
  const { data: allGroups = [] } = useChannels();
  const createMonitor = useCreateMonitor();
  const setMonitorGroups = useSetMonitorChannels();
  const saving = createMonitor.isPending || setMonitorGroups.isPending;

  // First-time defaults for the locations field — runs once when locations
  // arrive from the cache/network.
  useEffect(() => {
    if (!locations || defaultLocations.length > 0) return;
    const first = locations.map((l) => l.slug).slice(0, 1);
    setDefaultLocations(first);
    setForm((prev) => ({ ...prev, locations: first }));
  }, [locations, defaultLocations.length]);

  function toggleGroup(id) {
    const sid = String(id);
    setSelectedGroupIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  }

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function setOpt(key, value) {
    setForm((prev) => ({
      ...prev,
      options: { ...prev.options, [key]: value },
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const created = await createMonitor.mutateAsync(form);
      if (selectedGroupIds.length > 0) {
        await setMonitorGroups.mutateAsync({
          monitorId: created.id,
          channelIds: selectedGroupIds,
        });
      }
      toast.success(t("modal.success", { name: created.name }));
      onCreated(created);
      if (created.cron_token) {
        setCreatedCronToken(created.cron_token);
        return; // stay open to show the ping URL
      }
      onClose();
    } catch {
      toast.error(t("modal.error"));
    }
  }

  const isCron = form.type === "cron";

  function applyTemplate(template) {
    setForm((prev) => ({
      ...prev,
      ...normalizeTemplateToForm(template, defaultLocations),
    }));
    setShowTemplatePicker(false);
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-lg flex-col rounded-none bg-white p-0 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">{t("modal.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Cron ping URL confirmation */}
        {createdCronToken && (
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-sm font-semibold text-green-800">
                {t("modal.created_title")}
              </p>
              <p className="mt-1 text-xs text-green-700">{t("modal.created_info")}</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("modal.ping_url")}
              </label>
              <div className="flex items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
                <code className="flex-1 break-all font-mono text-xs text-gray-800">
                  {window.location.origin}/api/v1/cron/{createdCronToken}
                </code>
                <Button
                  variant="light"
                  size="xs"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/api/v1/cron/${createdCronToken}`
                    );
                    toast.success(t("modal.copied"));
                  }}
                >
                  {t("modal.copy")}
                </Button>
              </div>
              <p className="mt-1.5 text-xs text-gray-400">{t("modal.ping_url_help")}</p>
            </div>
            <div className="flex justify-end pt-2">
              <Button type="button" onClick={onClose}>
                {t("modal.done")}
              </Button>
            </div>
          </div>
        )}

        {/* Scrollable body */}
        {!createdCronToken && (
          <form
            onSubmit={handleSubmit}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              {showTemplatePicker && (
                <TemplatePicker
                  templatesByCategory={templatesByCategory}
                  onUseTemplate={(template) => {
                    applyTemplate(template);
                    toast.success(`${t("common:common.loaded")}: ${template.name}`);
                  }}
                />
              )}

              {/* Name */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {t("modal.name")}
                </label>
                <TextInput
                  required
                  value={form.name}
                  onValueChange={(v) => set("name", v)}
                  placeholder="My API"
                />
              </div>

              {/* Type + Interval */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t("modal.type")}
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      set("type", newType);
                      set("options", {});
                      set("interval_seconds", defaultIntervalFor(newType));
                    }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
                  >
                    {TYPE_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {typeLabel(t)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t("modal.interval")}
                  </label>
                  <IntervalSelect
                    value={form.interval_seconds}
                    onChange={(v) => set("interval_seconds", v)}
                  />
                </div>
              </div>

              {/* Target */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {isCron ? t("modal.job_label") : t("modal.target")}
                </label>
                <TextInput
                  required
                  value={form.target}
                  onValueChange={(v) => set("target", v)}
                  placeholder={TARGET_PLACEHOLDER[form.type]}
                />
                {isCron && (
                  <p className="mt-1 text-xs text-gray-400">{t("modal.cron_help")}</p>
                )}
              </div>

              {/* Type-specific options */}
              {form.type === "http" && (
                <HttpOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "ssl" && (
                <SslOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "domain" && (
                <DomainOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "dns" && (
                <DnsOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "tcp" && (
                <TcpOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "ping" && (
                <PingOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "port" && (
                <PortOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "api" && (
                <ApiOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "cron" && (
                <CronOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "playwright" && (
                <PlaywrightOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "shopify" && (
                <ShopifyOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "wordpress" && (
                <WordPressOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "visual_change" && (
                <VisualChangeOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "page_speed" && (
                <PageSpeedOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "seo_health" && (
                <SeoHealthOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "broken_links" && (
                <BrokenLinksOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "image_optimization" && (
                <ImageOptimizationOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "performance_budget" && (
                <PerformanceBudgetOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "suspicious_script" && (
                <SuspiciousScriptOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "carbon_footprint" && (
                <CarbonFootprintOptions options={form.options} onChange={setOpt} />
              )}
              {form.type === "platform_fingerprint" && (
                <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Browser Options
                  </p>
                  <ViewportSelect
                    value={form.options.viewport}
                    onChange={(v) => setOpt("viewport", v)}
                  />
                </div>
              )}
              {["wix", "webflow", "drupal", "joomla"].includes(form.type) && (
                <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Browser Options
                  </p>
                  <ViewportSelect
                    value={form.options.viewport}
                    onChange={(v) => setOpt("viewport", v)}
                  />
                </div>
              )}

              {/* Locations — hidden for cron (server-side check) */}
              {AGENT_TYPES.has(form.type) && (
                <LocationsField
                  locations={form.locations}
                  onChange={(v) => set("locations", v)}
                />
              )}

              {/* Confirmation count */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {t("modal.confirm_failure")}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={form.options.confirmation_count ?? 2}
                    onChange={(e) =>
                      setOpt("confirmation_count", Math.max(1, Number(e.target.value)))
                    }
                    className="w-20 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
                  />
                  <span className="text-sm text-gray-500">
                    {t("modal.consecutive_failures")}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-400">{t("modal.confirm_help")}</p>
              </div>

              {/* Contact groups */}
              {allGroups.length > 0 && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t("modal.channels")}
                  </label>
                  <div className="mt-1 space-y-1">
                    {allGroups.map((g) => (
                      <label
                        key={g.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedGroupIds.includes(String(g.id))}
                          onChange={() => toggleGroup(g.id)}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                        />
                        <span>{g.name}</span>
                        {g.member_count != null && (
                          <span className="text-xs text-gray-400">
                            ({t("modal.members_count", { count: g.member_count })})
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <Button variant="secondary" type="button" onClick={onClose}>
                {t("common:common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={
                  saving || (AGENT_TYPES.has(form.type) && form.locations.length === 0)
                }
              >
                {saving ? t("modal.creating") : t("modal.create")}
              </Button>
            </div>
          </form>
        )}
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Monitors list page
// ---------------------------------------------------------------------------

// Stable set of types for the filter dropdown. Kept independent of the loaded
// page so selecting a type filter doesn't collapse the dropdown to that type.
const MONITOR_TYPE_FILTERS = [
  "http",
  "ssl",
  "domain",
  "dns",
  "tcp",
  "ping",
  "port",
  "api",
  "cron",
  "playwright",
  "shopify",
  "wordpress",
  "page_speed",
  "visual_change",
];

export default function Monitors() {
  const { t } = useTranslation(["monitors", "common"]);
  const can = usePermission();
  const [showNew, setShowNew] = useState(false);
  const [newDefaults, setNewDefaults] = useState({
    type: undefined,
    target: undefined,
  });
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link support: /monitors?new=<type>&target=<target> (used by the
  // dashboard's recommended-monitor and first-run CTAs) opens the create
  // drawer prefilled, then strips the params so a refresh doesn't reopen it.
  useEffect(() => {
    const newType = searchParams.get("new");
    if (!newType) return;
    setNewDefaults({
      type: newType,
      target: searchParams.get("target") || undefined,
    });
    setShowNew(true);
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    next.delete("target");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Filters — initialised from and synced to the URL query string so they
  // survive navigating into a monitor and back (the component remounts, but
  // the URL params restore the prior state).
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("q") ?? "");
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get("type") ?? "all");
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") ?? "all"
  );

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const sync = (key, value, dflt) => {
          if (value && value !== dflt) next.set(key, value);
          else next.delete(key);
        };
        sync("q", searchTerm.trim(), "");
        sync("type", typeFilter, "all");
        sync("status", statusFilter, "all");
        return next;
      },
      { replace: true }
    );
  }, [searchTerm, typeFilter, statusFilter, setSearchParams]);

  // Debounce the search box so we issue one request after typing settles,
  // not one per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // Filters are applied server-side. The query key includes them, so changing
  // a filter resets pagination to the first page automatically.
  const queryParams = useMemo(() => {
    const p = { limit: 50 };
    if (typeFilter !== "all") p.type = typeFilter;
    if (statusFilter === "active") p.active = "true";
    else if (statusFilter === "paused") p.active = "false";
    const q = debouncedSearch.trim();
    if (q) p.q = q;
    return p;
  }, [typeFilter, statusFilter, debouncedSearch]);

  const monitorsQuery = useMonitorsInfinite(queryParams);
  const templatesQuery = useMonitorTemplates();
  const locationsQuery = useLocations();
  const updateMutation = useUpdateMonitor();
  const deleteMutation = useDeleteMonitor();

  const monitors = useMemo(
    () => (monitorsQuery.data?.pages ?? []).flatMap((p) => p.data ?? []),
    [monitorsQuery.data]
  );
  // Total matching the active filters, reported by the API (not just loaded).
  const totalCount = monitorsQuery.data?.pages?.[0]?.meta?.total ?? monitors.length;

  const templatesByCategory = templatesQuery.data ?? {};
  const locationLabels = useMemo(
    () => Object.fromEntries((locationsQuery.data ?? []).map((l) => [l.slug, l.label])),
    [locationsQuery.data]
  );

  const filtersActive =
    searchTerm.trim() !== "" || typeFilter !== "all" || statusFilter !== "all";

  const loading =
    monitorsQuery.isPending || templatesQuery.isPending || locationsQuery.isPending;

  async function handleToggle(monitor) {
    try {
      const updated = await updateMutation.mutateAsync({
        id: monitor.id,
        data: { is_active: !monitor.is_active },
      });
      toast.success(
        t("table.success_toggle", {
          name: updated.name,
          action: updated.is_active ? t("table.resumed") : t("table.paused_action"),
        })
      );
    } catch {
      toast.error(t("common:common.error"));
    }
  }

  async function handleDelete(id, name) {
    if (!confirm(t("table.delete_confirm", { name }))) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t("table.success_delete"));
    } catch {
      toast.error(t("common:common.error"));
    }
  }

  function handleCreated() {
    // Cache invalidation in useCreateMonitor refetches the list automatically.
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        </div>
        {can("monitors.create") && (
          <Button
            icon={Plus}
            onClick={() => {
              setNewDefaults({ type: undefined, target: undefined });
              setShowNew(true);
            }}
          >
            {t("new_monitor")}
          </Button>
        )}
      </div>

      {showNew && (
        <NewMonitorDrawer
          templatesByCategory={templatesByCategory}
          onClose={() => setShowNew(false)}
          onCreated={handleCreated}
          initialType={newDefaults.type}
          initialTarget={newDefaults.target}
        />
      )}

      {(monitors.length > 0 || filtersActive) && (
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("filters.search_placeholder")}
                className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              <option value="all">{t("filters.all_types")}</option>
              {MONITOR_TYPE_FILTERS.map((type) => (
                <option key={type} value={type}>
                  {t(`types.${type}`, { defaultValue: type })}
                </option>
              ))}
            </select>
            <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
              {[
                { value: "all", label: t("filters.all_status") },
                { value: "active", label: t("filters.active") },
                { value: "paused", label: t("filters.paused") },
              ].map((opt, i) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    statusFilter === opt.value
                      ? "bg-teal-50 text-teal-700"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  } ${i > 0 ? "border-l border-gray-200" : ""}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {filtersActive && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setTypeFilter("all");
                  setStatusFilter("all");
                }}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900"
              >
                <X className="h-3.5 w-3.5" />
                {t("filters.clear")}
              </button>
            )}
          </div>
          <span className="text-xs text-gray-400">
            {t("filters.showing", {
              count: monitors.length,
              total: totalCount,
            })}
          </span>
        </div>
      )}

      <Card className="p-0">
        {loading ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">{t("loading")}</p>
        ) : monitors.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">
            {filtersActive ? t("filters.no_matches") : t("empty_state")}
          </p>
        ) : (
          <>
            {/* Card list (used at every viewport) */}
            <div className="divide-y divide-gray-100">
              {monitors.map((m) => (
                <div key={m.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/monitors/${m.id}`}
                          className="text-sm font-semibold text-gray-900 hover:text-teal-600"
                        >
                          {m.name}
                        </Link>
                        <Badge color={m.is_active ? "green" : "stone"}>
                          {m.is_active ? t("table.active") : t("table.paused")}
                        </Badge>
                        <Badge color="gray">{t(`types.${m.type}`)}</Badge>
                        {m.channel_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <Users className="h-3.5 w-3.5" />
                            {m.channel_count}
                          </span>
                        )}
                        {m.escalation_policy_id && (
                          <Badge color="gray">
                            {t("table.escalation", { defaultValue: "Escalation" })}
                          </Badge>
                        )}
                      </div>
                      <p
                        className="mt-1 truncate text-xs text-gray-500"
                        title={m.target}
                      >
                        {m.target}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400">
                        <span>{fmtInterval(m.interval_seconds)}</span>
                        {m.locations?.length > 0 && (
                          <>
                            <span>·</span>
                            <div className="flex flex-wrap gap-1">
                              {m.locations.map((r) => (
                                <Badge key={r} color="gray">
                                  {locationLabels[r] ?? r}
                                </Badge>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {can("monitors.edit") && (
                        <Button
                          variant="secondary"
                          size="xs"
                          icon={m.is_active ? Pause : Play}
                          tooltip={m.is_active ? t("table.pause") : t("table.resume")}
                          onClick={() => handleToggle(m)}
                        />
                      )}
                      {can("monitors.delete") && (
                        <Button
                          variant="secondary"
                          size="xs"
                          color="red"
                          icon={Trash2}
                          tooltip={t("table.delete", { defaultValue: "Delete" })}
                          onClick={() => handleDelete(m.id, m.name)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {monitorsQuery.hasNextPage && (
              <div className="border-t border-gray-100 px-4 py-3 text-center">
                <button
                  type="button"
                  onClick={() => monitorsQuery.fetchNextPage()}
                  disabled={monitorsQuery.isFetchingNextPage}
                  className="rounded-md border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {monitorsQuery.isFetchingNextPage
                    ? t("loading")
                    : t("common:common.load_more")}
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
